import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Phone } from "lucide-react";
import { SPORTS, CONTACT } from "@/data/site";
import { Reveal } from "@/components/Reveal";

export default function SportPage() {
  const { slug } = useParams();
  const sport = SPORTS.find((s) => s.slug === slug);
  if (!sport) return <Navigate to="/programs" replace />;

  const Block = ({ title, items, note }) => (
    <div className="rounded-3xl border border-[#E2ECEE] bg-white p-7">
      <h3 className="font-head text-xl font-extrabold text-slate-900">{title}</h3>
      {note && <p className="mt-1 text-sm text-slate-500">{note}</p>}
      <ul className="mt-4 space-y-2.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 text-sm font-semibold text-slate-700">
            <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full text-white" style={{ backgroundColor: sport.accent }}>
              <Check className="h-3 w-3" />
            </span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <section className="relative overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${sport.accent}, #0B3B38)` }}>
        <div className="relative mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16">
          <Link to="/programs" className="inline-flex items-center gap-1.5 text-sm font-extrabold text-white/80 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All sports
          </Link>
          <div className="mt-6 flex flex-col items-start gap-5 sm:flex-row sm:items-center">
            <div className="h-28 w-28 flex-none overflow-hidden rounded-[1.75rem] bg-white/15 ring-1 ring-white/25 backdrop-blur">
              <img src={sport.image} alt={sport.name} className="h-full w-full object-cover" />
            </div>
            <div>
              <h1 className="font-head text-4xl font-extrabold sm:text-5xl">{sport.name}</h1>
              <p className="mt-1 text-base font-bold text-white/85">{sport.tagline}</p>
              <p className="mt-3 max-w-xl text-base text-white/85">{sport.intro}</p>
            </div>
          </div>
        </div>
      </section>

      <Reveal>
        <div className="mx-auto grid max-w-7xl gap-5 px-5 py-14 lg:grid-cols-3 lg:px-8">
          <Block title="What we teach" items={sport.whatWeTeach} />
          <Block title="How we teach it" items={sport.howWeTeach} note="Play first — skills follow." />
          <Block title="Skills built" items={sport.skills} />
        </div>
      </Reveal>

      <section className="mx-auto max-w-5xl px-5 pb-16 lg:px-8">
        <div className="flex flex-col items-center gap-4 rounded-[2rem] bg-[#0B3B38] p-8 text-center text-white sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h2 className="font-head text-2xl font-extrabold">Bring {sport.name} to your group</h2>
            <p className="mt-1 text-sm text-teal-50/80">Inquire for subsidised rates — tailored quote within 4 hours.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/inquire" className="inline-flex items-center gap-1.5 rounded-full bg-[#FACC15] px-6 py-3 font-head text-sm font-extrabold text-[#5A4300] hover:scale-105">
              Request a Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-1.5 rounded-full border-2 border-white/40 px-6 py-3 text-sm font-extrabold text-white hover:bg-white/10">
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
